<script lang="ts" setup>
import OrderDetailsPreview from '@consumer/modules/shared/OrderDetailsPreview.vue'
import CategoryHeader from '@consumer/modules/support/CategoryHeader.vue'
import OrderStatus from '@consumer/modules/buyer/OrderStatus.vue'
import type { SupportCategory } from '@consumer/types/SupportArticle'
import { formatCurrency } from '@consumer/utils/currency'
import { formatDate } from '@consumer/utils/date'
import { getTagline } from '@consumer/logic/giftly'
import type { PurchasedOrder, PurchasedOrderGift } from '@consumer/types/serializers'
import OrderGift from '@consumer/modules/support/OrderGift.vue'
import newGiftIcon from '@consumer/assets/icons/new/gift.svg?svgicon'
import type { BuyerOrRecipientGift, BuyerGift } from './support'

const { gift } = defineProps<{
  order?: PurchasedOrder
  gift?: BuyerOrRecipientGift
  currentCategory: SupportCategory
}>()

const tagline = $computed(() => gift && getTagline(gift))

function isBuyerGift (gift: BuyerOrRecipientGift): gift is BuyerGift {
  return 'statusLabel' in gift
}
</script>

<template>
  <div>
    <CategoryHeader :icon="currentCategory.icon" class="bg-primary-pale">
      <template v-if="order">
        <span class="text-v2-secondary">Support for</span>
        <br>
        Order #{{ order.orderNumber }}
      </template>
      <template v-else-if="gift && isBuyerGift(gift)">
        <span class="text-v2-secondary">Support for</span>
        <br>
        Order #{{ gift.orderNumber }}
      </template>
      <span v-else>
        Gift Support
      </span>

      <template #icon>
        <GIcon
          :svg="newGiftIcon"
          color="grey-500"
          size="120px"
          class="
          absolute lg:-top-30px right-0 !stroke-8 stroke-cap-round stroke-join-round opacity-20
          !w-120px !h-120px
          !lg:w-200px !lg:h-200px
        "
        />
      </template>
    </CategoryHeader>

    <!-- Buyer/order -->
    <div
      v-if="order"
      class="my-6 flex flex-col gap-6"
    >
      <template v-if="gift">
        <OrderGift
          :order="order"
          :orderGift="(gift as PurchasedOrderGift)"
        />
      </template>
      <template v-else>
        <OrderGift
          v-for="orderGift in order.orderGifts"
          :key="orderGift.uuid"
          :order="order"
          :orderGift="orderGift"
          linkToSupportPage
        />
      </template>
    </div>
    <!-- Recipient/gift -->
    <div
      v-else-if="gift"
      class="bg-grey-300 mt-6 p-4 flex items-center flex-col sm:flex-row gap-4 sm:gap-8 rounded-md mb-6"
    >
      <div class="w-full flex items-center justify-center" style="max-width: 176px;">
        <OrderDetailsPreview :gift="gift"/>
      </div>
      <div>
        <ul class="space-y-2">
          <li v-if="isBuyerGift(gift)">
            <span class="font-semibold">Purchased</span>
            {{ formatDate(gift.purchasedAt, { format: "MMM d, yyyy" }) }}
          </li>
          <li>
            <span class="font-semibold">Gift Amount</span>
            {{ formatCurrency(gift.amount!) }}
          </li>
          <li v-if="tagline">
            <span class="font-semibold">Gift Details </span>
            <span class="whitespace-pre-wrap">{{ tagline }}</span>
          </li>
          <li v-if="isBuyerGift(gift)">
            <span class="font-semibold">Status </span>
            <OrderStatus :gift="gift" class="inline ml-2"/>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
